<template @keydown.esc="scheduledTimeDialogOpened = false">
  <footer class="message-editor">
    <input
      type="file"
      style="display: none"
      ref="selectedDocuments"
      multiple
      @change="onDocumentsPicked"
    />

    <!-- Dialog: Schedule time -->
    <sc-dialog
      v-if="scheduledTimeDialogOpened"
      title="Schedule message"
      :is-narrow="true"
    >
      <header slot="header">
        <sc-button
          icon-left="close"
          is-icon-only="true"
          type="ghost"
        ></sc-button>
      </header>
      <sc-flex :is-column="true" :gap="16">
        <sc-p>{{ timeZone }}</sc-p>
        <date-picker
          v-model="scheduledTime"
          type="datetime"
          :showSecond="false"
          inline
        />
      </sc-flex>
      <footer slot="footer">
        <sc-button-group>
          <sc-button
            type="primary"
            @click="sendMessage()"
            :disabled="!scheduledTime"
            :loading="sending"
            >Schedule Message</sc-button
          >
          <sc-button
            type="secondary"
            icon-left="check"
            @click="
              scheduledTimeDialogOpened = false;
              scheduledTime = null;
            "
            >Cancel</sc-button
          >
        </sc-button-group>
      </footer>
    </sc-dialog>

    <!-- Dialog: Documents Dialog -->
    <v-dialog
      width="600"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      v-model="importDocumentsDialogOpened"
      persistent
      @keydown.esc="importDocumentsDialogOpened = false"
    >
      <v-card flat @drop.prevent="addFile" @dragover.prevent>
        <v-card-title class="title">
          {{ $t('inbox.attachDocument') }}
        </v-card-title>
        <v-divider />
        <v-alert
          :value="true"
          outline
          block
          :icon="$vuetify.breakpoint.smAndUp ? 'info' : ''"
          color="primary"
          class="text-xs-left mb-4"
          :class="$vuetify.breakpoint.smAndUp ? 'mx-4' : 'mx-2'"
        >
          {{ $t('alerts.dragAndDropFiles') }}
        </v-alert>
        <v-card-text class="mb-1 mt-1">
          <v-layout column>
            <v-flex class="text-xs-left mb-2 font-weight-bold">
              <v-layout align-center>
                <div class="py-3">{{ $t('documents.selectedDocuments') }}:</div>
                <v-spacer />
                <v-progress-circular
                  v-if="documentImporting"
                  indeterminate
                  size="25"
                  class="primary--text mr-2"
                />
                <v-btn
                  outline
                  class="default-input text-capitalize primary--text"
                  :disabled="documentImporting"
                  @click="pickDocuments"
                >
                  <v-layout align-center>
                    <v-icon class="mr-2" size="20">add</v-icon>
                    {{ $t('documents.selectFiles') }}
                  </v-layout>
                </v-btn>
              </v-layout>
            </v-flex>
            <v-flex class="text-xs-left mb-4" v-if="isEmpty(documentsToImport)">
              {{ $t('documents.noDocumentsSelected') }}
            </v-flex>
            <v-flex class="mb-4" v-else>
              <v-layout
                align-center
                :key="i"
                v-for="(doc, i) in documentsToImport"
              >
                <v-icon color="primary"> insert_drive_file </v-icon>
                <span class="ml-1 my-1">{{ doc.name }}</span>
                <v-spacer />
                <v-icon
                  @click="documentsToImport.splice(i, 1)"
                  style="cursor: pointer"
                  color="error"
                  >cancel</v-icon
                >
              </v-layout>
            </v-flex>
          </v-layout>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            class="text-capitalize primary"
            @click="importDocuments()"
            :disabled="documentImporting || isEmpty(documentsToImport)"
          >
            {{ $t('documents.uploadAll') }}
          </v-btn>
          <v-btn
            class="text-capitalize primary--text"
            outline
            @click="
              importDocumentsDialogOpened = false;
              documentsToImport = [];
            "
          >
            {{ $t('cancel') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Partial: Editor -->
    <quill-editor
      v-model="newMessage"
      :options="editorOption"
      :disabled="sending"
      class="message-editor__quill"
    />

    <!-- Partial: Options -->
    <div class="message-editor__actions">
      <!-- Partial: Actions -->
      <sc-button-group>
        <!-- Partial: Attach to single  -->
        <sc-button
          v-if="conversationCompanies.length === 1"
          @click="
            openImportDocumentsDialog();
            selectedCompanyId = conversationCompanies[0].id;
          "
          size="small"
          :is-icon-only="true"
          :with-tooltip="true"
          icon-left="attachment"
        >
          Attach
        </sc-button>

        <!-- Partial: Attach with multiple  -->
        <v-menu transition="slide-y-transition" bottom left v-else>
          <sc-button
            slot="activator"
            size="small"
            icon-left="attachment"
            :is-icon-only="true"
            :with-tooltip="true"
          >
            Attach
          </sc-button>

          <v-list>
            <sc-small class="message-editor-menu-small">
              Attach Document for Which Entity?
            </sc-small>
            <v-list-tile
              v-for="(company, i) in conversationCompanies"
              :key="i"
              @click="
                selectedCompanyId = company.id;
                openImportDocumentsDialog();
              "
            >
              <v-list-tile-title>
                {{ company.name }} ({{ company.entity_type }})
              </v-list-tile-title>
            </v-list-tile>
          </v-list>
        </v-menu>

        <!-- Action: Schedule -->
        <sc-button
          @click="openScheduleDialog()"
          :is-disabled="isScheduleDisabled"
          :is-icon-only="true"
          :with-tooltip="true"
          icon-left="calendar"
          size="small"
        >
          Schedule
        </sc-button>

        <!-- Action: Send -->
        <sc-button
          @click="sendMessage()"
          type="primary"
          size="small"
          icon-left="send"
          :is-disabled="!newMessage"
          :is-loading="sending"
        >
          Send
        </sc-button>
      </sc-button-group>
    </div>

    <!-- Partial: Reminder checkbox -->
    <div class="message-editor__reminder">
      <v-tooltip bottom right>
        <v-checkbox
          v-model="remindClient"
          label="Auto-remind"
          hide-details
          slot="activator"
        />
        <span
          >Client will receive a reminder every 3 days for a month if not they
          do not reply. Any new message will remove the reminder.</span
        >
      </v-tooltip>
    </div>
  </footer>
</template>

<script>
import moment from 'moment';
import API from '@/api';
import isEmpty from 'lodash/isEmpty';
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';

export default {
  props: {
    currentUser: {
      required: true,
      type: Object,
    },
    conversationCompanies: {
      type: Array,
      default: () => [],
    },
    selectedConversationId: {
      type: Number,
      default: null,
    },
  },
  components: {
    DatePicker,
  },

  data() {
    return {
      newMessage: '',
      scheduledTime: null,
      messageTag: 'general',
      remindClient: false,
      selectedCompanyId: null,
      sending: false,
      scheduledTimeDialogOpened: false,

      importDocumentsDialogOpened: false,
      documentsToImport: [],
      documentImporting: false,

      editorOption: {
        formats: ['bold', 'italic', 'underline', 'link', 'indent', 'list'],
        modules: {
          toolbar:
            window.innerWidth >= 768
              ? [
                  ['bold', 'italic', 'underline'],
                  [{ indent: '-1' }, { indent: '+1' }],
                  ['link'],
                  [{ list: 'ordered' }, { list: 'bullet' }],
                ]
              : [['bold', 'italic', 'underline']],
        },
        placeholder: this.$t('inbox.message'),
        bounds: '.message-input',
      },
    };
  },

  computed: {
    timeZone() {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    },
    isScheduleDisabled() {
      return !this.newMessage || this.sending;
    },
  },

  methods: {
    isEmpty,
    openScheduleDialog() {
      if (this.isScheduleDisabled) return;

      this.scheduledTimeDialogOpened = true;
    },
    clearMessage(message) {
      return message
        .trim()
        .split('\n')
        .join('<br/>');
    },
    async sendMessage() {
      if (this.newMessage && !this.sending) {
        this.sending = true;

        const response = await API.conversation.sendMessage({
          text: this.clearMessage(this.newMessage),
          conversationId: this.selectedConversationId,
          hasReminder: this.remindClient,
          messageTag: this.messageTag,
          scheduledTime: this.scheduledTime
            ? moment(this.scheduledTime)
                .utc()
                .toISOString()
            : null,
        });

        if (response.data.error) {
          this.$store.dispatch('application/setErrorNotification', {
            message: 'There was a problem.',
          });
          console.log(response.data.error);
        } else {
          this.$emit('messageSent');
          this.sending = false;
          this.newMessage = '';
          this.scheduledTimeDialogOpened = false;
          this.scheduledTime = null;
        }
      }
    },

    async onDocumentsPicked(e) {
      this.documentImporting = true;
      const documents = e.target.files;

      if (documents) {
        this.documentsToImport.push(...documents);
      }

      this.documentImporting = false;
    },

    async openImportDocumentsDialog() {
      this.documentsToImport = [];
      this.documentImporting = false;
      this.importDocumentsDialogOpened = true;
    },

    async importDocuments() {
      this.sending = true;
      this.importDocumentsDialogOpened = false;
      let path = '';
      try {
        for (const documentToImport of this.documentsToImport) {
          const companyId = this.selectedCompanyId
            ? this.selectedCompanyId
            : this.conversationCompanies[0].id;

          if (
            this.selectedCompanyId &&
            !this.conversationCompanies
              .map(c => c.id)
              .includes(parseInt(this.selectedCompanyId))
          ) {
            console.error('selectedCompanyId cannot be found in conversations');
            return this.$store.dispatch('application/setErrorNotification', {
              message: 'Something went wrong but we have been notified',
            });
          }

          const {
            data: { id, upload_link },
          } = await API.integration.getTemporaryUploadLink({
            companyId,
          });

          await API.integration.uploadToDropbox(upload_link, documentToImport);

          const response = await API.integration.processDocument({
            id,
            documentName: documentToImport.name,
            companyId,
            path: '/Attachments',
            conversationId: this.selectedConversationId,
          });

          if (response.data.error) {
            console.log(response.data.error);
          } else if (isEmpty(path)) {
            path = response.data.document_location;
          } else {
            path += '&!&'.concat(`${response.data.document_location}`);
          }
        }
        this.$store.dispatch('application/setSuccessNotification', {
          message: `You have successfully uploaded ${
            this.documentsToImport.length
          } document${this.documentsToImport.length > 1 ? 's' : ''}!`,
        });
        const response2 = await API.conversation.sendMessage({
          text: `$$attached$$${path}`,
          conversationId: this.selectedConversationId,
        });
        if (response2.data.error) {
          this.$store.dispatch('application/setErrorNotification', {
            message: 'There was a problem.',
          });
          console.log(response2.data.error);
        } else {
          this.$emit('messageSent');
        }
      } catch (error) {
        console.error(error);
        this.$store.dispatch('application/setErrorNotification', {
          message: 'Attachment unsuccessful. Please try again.',
        });
      } finally {
        this.sending = false;
        this.documentsToImport = [];
      }
    },

    pickDocuments() {
      const input = this.$refs.selectedDocuments;
      input.type = 'text';
      input.type = 'file';
      this.$refs.selectedDocuments.click();
    },

    async addFile(e) {
      this.importDocumentsDialogOpened = true;
      this.documentImporting = true;
      let droppedFiles = e.dataTransfer.files;
      if (!droppedFiles) return;

      this.documentsToImport.push(...droppedFiles);
      this.documentImporting = false;
    },

    removeFile(file) {
      this.files = this.files.filter(f => {
        return f !== file;
      });
    },
  },
};
</script>

<style lang="css" scoped="true">
.message-editor {
  position: relative;

  display: flex;
  padding-bottom: 42px;
}

.message-editor__quill {
  flex-grow: 1;
  margin: -1px;
  width: 100%;
}

.message-editor__actions {
  position: absolute;
  right: 4px;
  top: 4px;

  align-items: center;
  display: flex;
}

.message-editor__reminder {
  position: absolute;
  right: 8px;
  bottom: 8px;
}

.message-editor-menu-small {
  display: block;
  padding: 8px 4px;
}
</style>
<template @keydown.esc="scheduledTimeDialogOpened = false">
  <footer class="message-editor">
    <input
      type="file"
      style="display: none"
      ref="selectedDocuments"
      multiple
      @change="onDocumentsPicked"
    />

    <!-- Dialog: Schedule time -->
    <sc-dialog
      v-if="scheduledTimeDialogOpened"
      title="Schedule message"
      :is-narrow="true"
    >
      <header slot="header">
        <sc-button
          icon-left="close"
          is-icon-only="true"
          type="ghost"
        ></sc-button>
      </header>
      <sc-flex :is-column="true" :gap="16">
        <sc-p>{{ timeZone }}</sc-p>
        <date-picker
          v-model="scheduledTime"
          type="datetime"
          :showSecond="false"
          inline
        />
      </sc-flex>
      <footer slot="footer">
        <sc-button-group>
          <sc-button
            type="primary"
            @click="sendMessage()"
            :disabled="!scheduledTime"
            :loading="sending"
            >Schedule Message</sc-button
          >
          <sc-button
            type="secondary"
            icon-left="check"
            @click="
              scheduledTimeDialogOpened = false;
              scheduledTime = null;
            "
            >Cancel</sc-button
          >
        </sc-button-group>
      </footer>
    </sc-dialog>

    <!-- Dialog: Documents Dialog -->
    <v-dialog
      width="600"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      v-model="importDocumentsDialogOpened"
      persistent
      @keydown.esc="importDocumentsDialogOpened = false"
    >
      <v-card flat @drop.prevent="addFile" @dragover.prevent>
        <v-card-title class="title">
          {{ $t('inbox.attachDocument') }}
        </v-card-title>
        <v-divider />
        <v-alert
          :value="true"
          outline
          block
          :icon="$vuetify.breakpoint.smAndUp ? 'info' : ''"
          color="primary"
          class="text-xs-left mb-4"
          :class="$vuetify.breakpoint.smAndUp ? 'mx-4' : 'mx-2'"
        >
          {{ $t('alerts.dragAndDropFiles') }}
        </v-alert>
        <v-card-text class="mb-1 mt-1">
          <v-layout column>
            <v-flex class="text-xs-left mb-2 font-weight-bold">
              <v-layout align-center>
                <div class="py-3">{{ $t('documents.selectedDocuments') }}:</div>
                <v-spacer />
                <v-progress-circular
                  v-if="documentImporting"
                  indeterminate
                  size="25"
                  class="primary--text mr-2"
                />
                <v-btn
                  outline
                  class="default-input text-capitalize primary--text"
                  :disabled="documentImporting"
                  @click="pickDocuments"
                >
                  <v-layout align-center>
                    <v-icon class="mr-2" size="20">add</v-icon>
                    {{ $t('documents.selectFiles') }}
                  </v-layout>
                </v-btn>
              </v-layout>
            </v-flex>
            <v-flex class="text-xs-left mb-4" v-if="isEmpty(documentsToImport)">
              {{ $t('documents.noDocumentsSelected') }}
            </v-flex>
            <v-flex class="mb-4" v-else>
              <v-layout
                align-center
                :key="i"
                v-for="(doc, i) in documentsToImport"
              >
                <v-icon color="primary"> insert_drive_file </v-icon>
                <span class="ml-1 my-1">{{ doc.name }}</span>
                <v-spacer />
                <v-icon
                  @click="documentsToImport.splice(i, 1)"
                  style="cursor: pointer"
                  color="error"
                  >cancel</v-icon
                >
              </v-layout>
            </v-flex>
          </v-layout>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            class="text-capitalize primary"
            @click="importDocuments()"
            :disabled="documentImporting || isEmpty(documentsToImport)"
          >
            {{ $t('documents.uploadAll') }}
          </v-btn>
          <v-btn
            class="text-capitalize primary--text"
            outline
            @click="
              importDocumentsDialogOpened = false;
              documentsToImport = [];
            "
          >
            {{ $t('cancel') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Partial: Editor -->
    <quill-editor
      v-model="newMessage"
      :options="editorOption"
      :disabled="sending"
      class="message-editor__quill"
    />

    <!-- Partial: Options -->
    <div class="message-editor__actions">
      <!-- Partial: Actions -->
      <sc-button-group>
        <!-- Partial: Attach to single  -->
        <sc-button
          v-if="conversationCompanies.length === 1"
          @click="
            openImportDocumentsDialog();
            selectedCompanyId = conversationCompanies[0].id;
          "
          size="small"
          :is-icon-only="true"
          :with-tooltip="true"
          icon-left="attachment"
        >
          Attach
        </sc-button>

        <!-- Partial: Attach with multiple  -->
        <v-menu transition="slide-y-transition" bottom left v-else>
          <sc-button
            slot="activator"
            size="small"
            icon-left="attachment"
            :is-icon-only="true"
            :with-tooltip="true"
          >
            Attach
          </sc-button>

          <v-list>
            <sc-small class="message-editor-menu-small">
              Attach Document for Which Entity?
            </sc-small>
            <v-list-tile
              v-for="(company, i) in conversationCompanies"
              :key="i"
              @click="
                selectedCompanyId = company.id;
                openImportDocumentsDialog();
              "
            >
              <v-list-tile-title>
                {{ company.name }} ({{ company.entity_type }})
              </v-list-tile-title>
            </v-list-tile>
          </v-list>
        </v-menu>

        <!-- Action: Schedule -->
        <sc-button
          @click="openScheduleDialog()"
          :is-disabled="isScheduleDisabled"
          :is-icon-only="true"
          :with-tooltip="true"
          icon-left="calendar"
          size="small"
        >
          Schedule
        </sc-button>

        <!-- Action: Send -->
        <sc-button
          @click="sendMessage()"
          type="primary"
          size="small"
          icon-left="send"
          :is-disabled="!newMessage"
          :is-loading="sending"
        >
          Send
        </sc-button>
      </sc-button-group>
    </div>

    <!-- Partial: Reminder checkbox -->
    <div class="message-editor__reminder">
      <v-tooltip bottom right>
        <v-checkbox
          v-model="remindClient"
          label="Auto-remind"
          hide-details
          slot="activator"
        />
        <span
          >Client will receive a reminder every 3 days for a month if not they
          do not reply. Any new message will remove the reminder.</span
        >
      </v-tooltip>
    </div>
  </footer>
</template>

<script>
import moment from 'moment';
import API from '@/api';
import isEmpty from 'lodash/isEmpty';
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';

export default {
  props: {
    currentUser: {
      required: true,
      type: Object,
    },
    conversationCompanies: {
      type: Array,
      default: () => [],
    },
    selectedConversationId: {
      type: Number,
      default: null,
    },
  },
  components: {
    DatePicker,
  },

  data() {
    return {
      newMessage: '',
      scheduledTime: null,
      messageTag: 'general',
      remindClient: false,
      selectedCompanyId: null,
      sending: false,
      scheduledTimeDialogOpened: false,

      importDocumentsDialogOpened: false,
      documentsToImport: [],
      documentImporting: false,

      editorOption: {
        formats: ['bold', 'italic', 'underline', 'link', 'indent', 'list'],
        modules: {
          toolbar:
            window.innerWidth >= 768
              ? [
                  ['bold', 'italic', 'underline'],
                  [{ indent: '-1' }, { indent: '+1' }],
                  ['link'],
                  [{ list: 'ordered' }, { list: 'bullet' }],
                ]
              : [['bold', 'italic', 'underline']],
        },
        placeholder: this.$t('inbox.message'),
        bounds: '.message-input',
      },
    };
  },

  computed: {
    timeZone() {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    },
    isScheduleDisabled() {
      return !this.newMessage || this.sending;
    },
  },

  methods: {
    isEmpty,
    openScheduleDialog() {
      if (this.isScheduleDisabled) return;

      this.scheduledTimeDialogOpened = true;
    },
    async sendMessage() {
      if (this.newMessage && !this.sending) {
        this.sending = true;
        const response = await API.conversation.sendMessage({
          text: this.newMessage
            .trim()
            .split('\n')
            .join('<br/>'),
          conversationId: this.selectedConversationId,
          hasReminder: this.remindClient,
          messageTag: this.messageTag,
          scheduledTime: this.scheduledTime
            ? moment(this.scheduledTime)
                .utc()
                .toISOString()
            : null,
        });

        if (response.data.error) {
          this.$store.dispatch('application/setErrorNotification', {
            message: 'There was a problem.',
          });
          console.log(response.data.error);
        } else {
          this.$emit('messageSent');
          this.sending = false;
          this.newMessage = '';
          this.scheduledTimeDialogOpened = false;
          this.scheduledTime = null;
        }
      }
    },

    async onDocumentsPicked(e) {
      this.documentImporting = true;
      const documents = e.target.files;

      if (documents) {
        this.documentsToImport.push(...documents);
      }

      this.documentImporting = false;
    },

    async openImportDocumentsDialog() {
      this.documentsToImport = [];
      this.documentImporting = false;
      this.importDocumentsDialogOpened = true;
    },

    async importDocuments() {
      this.sending = true;
      this.importDocumentsDialogOpened = false;
      let path = '';
      try {
        for (const documentToImport of this.documentsToImport) {
          const companyId = this.selectedCompanyId
            ? this.selectedCompanyId
            : this.conversationCompanies[0].id;

          if (
            this.selectedCompanyId &&
            !this.conversationCompanies
              .map(c => c.id)
              .includes(parseInt(this.selectedCompanyId))
          ) {
            console.error('selectedCompanyId cannot be found in conversations');
            return this.$store.dispatch('application/setErrorNotification', {
              message: 'Something went wrong but we have been notified',
            });
          }

          const {
            data: { id, upload_link },
          } = await API.integration.getTemporaryUploadLink({
            companyId,
          });

          await API.integration.uploadToDropbox(upload_link, documentToImport);

          const response = await API.integration.processDocument({
            id,
            documentName: documentToImport.name,
            companyId,
            path: '/Attachments',
            conversationId: this.selectedConversationId,
          });

          if (response.data.error) {
            console.log(response.data.error);
          } else if (isEmpty(path)) {
            path = response.data.document_location;
          } else {
            path += '&!&'.concat(`${response.data.document_location}`);
          }
        }
        this.$store.dispatch('application/setSuccessNotification', {
          message: `You have successfully uploaded ${
            this.documentsToImport.length
          } document${this.documentsToImport.length > 1 ? 's' : ''}!`,
        });
        const response2 = await API.conversation.sendMessage({
          text: `$$attached$$${path}`,
          conversationId: this.selectedConversationId,
        });
        if (response2.data.error) {
          this.$store.dispatch('application/setErrorNotification', {
            message: 'There was a problem.',
          });
          console.log(response2.data.error);
        } else {
          this.$emit('messageSent');
        }
      } catch (error) {
        console.error(error);
        this.$store.dispatch('application/setErrorNotification', {
          message: 'Attachment unsuccessful. Please try again.',
        });
      } finally {
        this.sending = false;
        this.documentsToImport = [];
      }
    },

    pickDocuments() {
      const input = this.$refs.selectedDocuments;
      input.type = 'text';
      input.type = 'file';
      this.$refs.selectedDocuments.click();
    },

    async addFile(e) {
      this.importDocumentsDialogOpened = true;
      this.documentImporting = true;
      let droppedFiles = e.dataTransfer.files;
      if (!droppedFiles) return;

      this.documentsToImport.push(...droppedFiles);
      this.documentImporting = false;
    },

    removeFile(file) {
      this.files = this.files.filter(f => {
        return f !== file;
      });
    },
  },
};
</script>

<style lang="css" scoped="true">
.message-editor {
  position: relative;

  display: flex;
  padding-bottom: 42px;
}

.message-editor__quill {
  flex-grow: 1;
  margin: -1px;
  width: 100%;
}

.message-editor__actions {
  position: absolute;
  right: 4px;
  top: 4px;

  align-items: center;
  display: flex;
}

.message-editor__reminder {
  position: absolute;
  right: 8px;
  bottom: 8px;
}

.message-editor-menu-small {
  display: block;
  padding: 8px 4px;
}
</style>
